import type { AiCapabilityPage } from "../schema.js";
import { LIVE, MANAGER_ROLES, WRITE_FLAGS, SLOT_COLLECTION_AUTOFILL, SLOT_GROUP } from "./_shared.js";

/**
 * `DEBT-REMIND` (@UC0201 @UC0184) — nhắc RIÊNG từng người chưa đóng, T1 một chạm. Executor = `SendRemindersService.send`
 * (collections/send-reminders.service.ts:153) — cùng cửa với nút "Nhắc" trên màn Đợt thu (`POST /v1/collections/:id/reminders`).
 * **0 bút toán**: cửa này ghi `notification` + `outbox_events`, không chạm `ledger_*` (service tự ghi: *"KHÔNG post ledger"*).
 *
 * 🔴🔴 **KÊNH NHẮC LÀ RIÊNG TƯ TUYỆT ĐỐI, VÀ Ở ĐÂY NÓ ĐƯỢC GIỮ BẰNG HÌNH DẠNG — KHÔNG BẰNG MỘT CÂU HỨA.**
 * `CLAUDE.md` §7 (P5.3, QĐ PO 06/08/2026) tách hai kênh: kênh XEM cấu hình được theo nhóm, còn kênh NHẮC thì
 * *"không có cờ nào nới điều này"* — app đóng vai kẻ đòi, **không bao giờ broadcast tên người chưa đóng vào nhóm**.
 * Ở tầng Sổ, điều đó có nghĩa là trang **không có ô nào cho người dùng chọn kênh gửi**: 0 ô `enum`. Một ô `enum`
 * "gửi kiểu gì" — dù `choices` hôm nay chỉ có một giá trị — là chỗ để lần sau ai đó thêm *"đăng vào nhóm"* như một
 * lựa chọn hợp lệ; và từ 0054b, ô `enum` còn **ánh xạ được** vào `choices` (opt-in), nên lời mời đó sẽ tới được từ
 * một câu không hề nói ra nó. Không có ô thì câu hỏi ấy *không biểu diễn được*. Khoá ở `debt-remind.page.test.ts`.
 *
 * 🔴 **Ô `loiNhan` của bản `draft` ĐÃ GỠ — nó là ô người dùng điền rồi bị VỨT.** Đo trên cửa thật:
 * `SendRemindersInput` (`send-reminders.service.ts:47-51`) có ĐÚNG hai trường — `collectionId` · `groupMemberIds` —
 * và `sendRemindersRequestSchema` `.strict()` chỉ nhận `groupMemberIds`. Câu chữ của lời nhắc **không phải** thứ cửa
 * này nhận: nó render từ `NOTIFICATION_TEMPLATE_REGISTRY` (entry `reminder`) theo locale NGƯỜI NHẬN, qua đúng
 * `renderNotification` mà worker và màn Preview dùng — một bản chép thứ hai ở đây là hai kênh nói hai câu.
 * Cùng lớp lỗi với "ô ngày" mà `RECORD-PAID` cố ý KHÔNG khai, và nặng hơn ở chỗ đây là kênh NHẮC: người dùng tin
 * rằng lời nhắn của họ đã tới tay người bị nhắc, trong khi nó chưa bao giờ rời khỏi lượt chat.
 * (Muốn cho người dùng viết lời nhắn ⇒ phải mở trường đó ở CỬA trước — task khác, đụng cả template registry.)
 *
 * 🔴 **Ô `nhom` không phải trang trí** (cùng luật `RECORD-PAID`/`PAY-REPORT`, QĐ PO 17/09 16:05): tab trợ lý là tab
 * TOÀN CỤC, và ô `dot` loại `collection` có `AI_SLOT_TYPE_NEEDS_GROUP.collection = true`. Không khai ô nhóm ⇒
 * `assertCanOpen` trả 403 `AI_GROUP_REQUIRED` và `allowedPages` lọc trang khỏi menu ⇒ trang không mở được bằng chat.
 * Khai ĐẦU TIÊN vì phạm vi ô `dot` phụ thuộc nhóm đã chốt.
 *
 * 🔴🔴 **`soNguoi` và `soNguoiThat` là HAI con số, và trang phải nói cả hai.** `preview` đếm suất chưa đóng
 * (`pending`/`overdue`); `execute` trả `remindedCount` = số suất **CÓ ÍT NHẤT MỘT NGƯỜI NHẬN** — `send()` lọc
 * `seatRecipientUserIds(...).length > 0`. Hai số lệch nhau một cách **bình thường**: suất trẻ em chưa ai liên kết
 * tài khoản thì đếm vào "chưa đóng" nhưng không nhắc được ai (đúng ca mà PR #626 đã vá ở worker). Và `AiDoerService`
 * so TỪNG khoá của phiếu với kết quả (`FACTS_MISMATCH` ⇒ rollback), nên nếu `done` dùng lại `{soNguoi}` thì chỉ còn
 * hai đường, cả hai đều sai: executor trả lại con số PHIẾU ⇒ câu *"Đã nhắc riêng N người"* **nói dối** đúng số người
 * không nhận được gì; hoặc executor trả số thật ⇒ **lượt nhắc hợp lệ bị rollback**. ⇒ fact CHỈ-CÓ-Ở-EXECUTE
 * `soNguoiThat`, đúng khuôn `{ngayGhiThat}` của `RECORD-PAID` và `{hanThat}` của `COL-EQUAL`.
 *
 * 🔴 **KHÔNG khai `executorMayNoop`, có chủ đích.** `send()` **cố ý KHÔNG idempotent** (docblock service: *"gọi
 * reminders 2 lần = nhắc lại (re-nudge hợp lệ) … ĐỪNG thêm unique-key dedup"*) — nên không có nhánh replay-lặng nào
 * để `doneAlready` render tới; khai cờ là tạo một câu chết. Lượt thứ hai **ghi thật** một đợt notification nữa, và
 * đó là hành vi ĐÚNG: nhắc lại là việc có thật. Chặn spam là việc của `@Throttle(20/phút)` ở controller, không phải
 * của trang. (Trang vẫn qua phiếu xác nhận vì T1 — người bấm, không phải máy tự nhắc.)
 *
 * ⚠️ `remindedCount = 0` là kết quả HỢP LỆ (mọi người đã đóng, hoặc không suất nào có người nhận): câu `done` nói
 * con số thật, kể cả khi nó là 0 — không bịa ra một lời nhắc không tồn tại.
 */
export const DEBT_REMIND = {
  id: "DEBT-REMIND",
  uc: "@UC0201",
  tier: "T1",
  status: LIVE,
  // Mirror ĐÚNG `@RequireFundRoles(OWNER, ADMIN, TREASURER)` của `send-reminders.controller.ts:41` — không tập vai thứ hai.
  roles: MANAGER_ROLES,
  flags: WRITE_FLAGS,
  slots: [
    SLOT_GROUP,
    { ...SLOT_COLLECTION_AUTOFILL, id: "dot", required: true, ask: "Nhắc cho đợt thu nào?" },
    /*
     * 🔴 AI-INTERP-20261023 C3 (@UC0201 @UC0161) — ô NGƯỜI tuỳ chọn ⇔ trường `groupMemberIds` của cửa. Trước C3 «nhắc riêng mẹ bé Na» nhắc CẢ
     * đợt. TUỲ CHỌN + KHÔNG `autofill`: vắng chữ ⇒ engine bỏ qua ô (hành vi cũ — mọi suất chưa đóng); có chữ ⇒ resolver người sẵn có, phạm vi =
     * người PHẢI đóng của NHÓM (`memberScope` mặc định `obligation` — KHÔNG phải tập chưa đóng của đợt), 0 hoặc ≥2 khớp ⇒ HỎI, không đoán. Binding
     * mới thu về khoản CHƯA đóng của ĐỢT (`describeRemindScope` lọc theo ghế): người đã đóng ⇒ `AI_DEBT_REMIND_MEMBER_NOT_UNPAID`; suất chưa ai dùng app ⇒
     * `AI_DEBT_REMIND_MEMBER_NOT_REACHABLE` (review C3). Cụm chỉ CẢ NHÓM («mọi người», «các phụ huynh còn thiếu») ⇒ như vắng ô — OPT-IN `unnamedMeansBlank`
     * dưới + danh sách trắng `memberTextIsGroupPhrase` (`resolveSlot`); tên/đại từ không khớp («nó», «Định») vẫn HỎI. Không phải `enum`: kênh gửi vẫn không là lựa chọn (P5.3).
     */
    {
      id: "nguoi",
      type: "member",
      required: false,
      // re-review HIGH (@UC0201): bỏ trống ở ĐÂY = cả đợt ⇒ cụm chỉ cả nhóm («mọi người chưa đóng») là vắng ô, không hỏi «Nhắc riêng ai?».
      unnamedMeansBlank: true,
      ask: "Nhắc riêng ai? (bỏ trống = mọi người chưa đóng của đợt)",
      clarify: "Có nhiều người tên giống vậy — bạn muốn nhắc riêng ai?",
    },
  ],
  llm: {
    description:
      "GỬI ĐI lời nhắc riêng tới từng thành viên còn chưa đóng của một đợt thu; không đăng vào nhóm. Trang này HÀNH ĐỘNG (gửi thông báo cho người ta). Điều kiện vào đây là câu có ĐỘNG TỪ GỬI ĐI — nhắc/giục/ping/đòi/gửi lời nhắc. Chữ 'nợ' hay 'chưa đóng' một mình KHÔNG đủ: câu chỉ HỎI hoặc LIỆT KÊ (\"ai chưa đóng\", \"mấy bạn còn nợ đợt thu\", \"còn ai chưa nộp\") là XEM và thuộc READ-COLLECTIONS, dù nó nói về đúng những người này.",
    examples: [
      "nhắc mọi người đóng tiền đợt thu",
      "nhac moi nguoi dong tien",
      "nhắc những bạn chưa đóng quỹ tháng 9",
      "nhac nhung ban chua dong quy thang 9",
      "gửi nhắc nhở đóng tiền dã ngoại",
      "gui nhac nho dong tien",
      "nhắc đóng tiền",
      "ping mấy người chưa nộp",
      "nhắc nhẹ các phụ huynh còn thiếu",
      "nhắc nợ",
      "gửi lời nhắc đóng quỹ cho ai chưa đóng",
      "nhắc quỹ",
      // ——— 0105: thêm cho đủ 40 ———
      "nhac dong tien",
      "nhac no",
      "nhac quy",
      "gui loi nhac dong quy cho ai chua dong",
      "ping may nguoi chua nop",
      "nhac nhe cac phu huynh con thieu",
      "gửi nhắc cho người chưa nộp đợt dã ngoại",
      "gui nhac cho nguoi chua nop dot da ngoai",
      "nhắc riêng từng người chưa đóng",
      "nhac rieng tung nguoi chua dong",
      "hối mọi người đóng quỹ đi",
      "hoi moi nguoi dong quy di",
      "bắn nhắc đợt thu tháng này",
      "ban nhac dot thu thang nay",
      "gửi thông báo nhắc nộp tiền",
      "gui thong bao nhac nop tien",
      "nhắc các bạn còn thiếu tiền quỹ lớp",
      "nhac cac ban con thieu tien quy lop",
      "nhắc giúp mình mấy người chưa đóng nhé",
      "nhac giup minh may nguoi chua dong nhe",
      "gửi nhắc lại lần nữa",
      "gui nhac lai lan nua",
      "nhắc lại đợt thu tuần trước",
      "nhac lai dot thu tuan truoc",
      "giục người chưa nộp",
      "giuc nguoi chua nop",
      "nhắc đóng quỹ đội bóng",
      "nhac dong quy doi bong",
      // AI-INTERP C3/C5: nhắc riêng MỘT người (ô `nguoi`).
      // (Cố ý KHÔNG dùng "mẹ bé …"/"anh …": hai cụm đó kéo BM25 của trang này lên trên trang khoá ở câu đổi vai / C08 — locked-page-hint.test.)
      "nhắc riêng Na đóng tiền đợt này",
      "nhac rieng Na dong tien dot nay",
      "nhắc chị Lan đóng quỹ giúp mình",
      "nhac chi Lan dong quy giup minh",
      "giục Hùng nộp tiền đi",
      "giuc Hung nop tien di",
    ],
    // AI-INTERP M12 (QĐ I12): câu THỬ — KHÔNG vào prompt/BM25; viết đời thường, không chép ví dụ (validator HELD_OUT_IS_EXAMPLE).
    // (Câu thử không vào BM25 ⇒ «mẹ bé …» ở đây không kéo trang này lên trên trang khoá — lý do của ví dụ ở trên không áp.)
    heldOutExamples: [
      "nhắc riêng mẹ bé Na đóng quỹ giùm em nha",
      "nhac rieng me be Na dong tien dum tui",
      "ping may ban chua ck tien quy di em",
      "hối mấy nhà còn thiếu tiền đợt này giúp chị nhé",
      "giuc nha be Bin nop quy di, tre han roi",
      "nhắn nhắc ai chưa đóng quỹ tháng này hộ cô",
    ],
  },
  executor: "sendReminders.send",
  /**
   * 🔴 US-AIV2-0113 M04 (@UC0239 · QĐ PO Q3 = CÓ 28/09) — màn GUI làm CÙNG việc cho suất KHÔNG ai nhận được lời nhắc trong app: "Nhắc
   * qua Zalo" (mỗi người MỘT câu riêng qua share sheet — chủ nhóm tự chọn người nhận; app không gửi đi đâu). `:collectionId` chỉ binding
   * biết: lượt `done` khai qua `__guiScreen` (còn suất không ai nhận), lượt lỗi `NO_REACHABLE_MEMBER` / `MEMBER_NOT_REACHABLE` (C3: một người) qua tham số lỗi (bảng đóng
   * `FAILURE_GUI_SCREEN`). KHÔNG vào prompt/chỉ mục (không đổi ca eval).
   */
  guiScreen: "/groups/:groupId/collections/:collectionId/remind-zalo",
  screenShows: "zalo_reminder",
  /** `@UC0216` — cửa mobile trang này chịu trách nhiệm: kênh NHẮC riêng tư P5.3 (`groups_endpoints.dart:301`) + danh sách nhắc tay (M04). */
  endpoints: [
    "POST /collections/{collectionId}/reminders",
    "GET /collections/{collectionId}/reminders/share-targets",
  ],
  facts: ["soNguoi", "tenDot", "soNguoiThat", "soNhanDuoc", "tenNguoiNhac", "phamViNhac"],
  templates: {
    // Phiếu nói con số ƯỚC (đếm lúc preview) — `{soNguoiThat}` chưa tồn tại lúc này, dùng ở đây là phiếu VỠ.
    // 🔴 US-AIV2-0109 S03 (@UC0239): nói CẢ số suất nhắc TỚI được (`soNhanDuoc`, cùng vị-từ `send()`). E2E 25/09: phiếu hứa
    // "3 người" rồi câu xong mới lộ "đã nhắc 0 người" — suất trẻ em chưa ai cài app đếm vào số thứ nhất mà không nhận được gì.
    // AI-INTERP C3: câu nêu một người ⇒ vế «— chỉ gửi cho {tenNguoiNhac}» (đoạn tuỳ chọn); vắng người ⇒ vế biến mất (nhắc cả đợt như cũ).
    // 🔴 C3 re-review sec M-1: đầu câu `{phamViNhac}` (binding đặt theo phạm vi — «Nhắc TẤT CẢ người chưa đóng của» / «Nhắc riêng một người của»): phiếu cả đợt
    // nói RÕ là cả đợt, không mở bằng «Nhắc riêng» (người vừa nói một người mà ô bị coi là vắng sẽ đọc nhầm thành «nhắc riêng người đó»).
    confirm: "{phamViNhac} “{tenDot}”[[ — chỉ gửi cho {tenNguoiNhac}]]: {soNguoi} suất chưa đóng, trong đó {soNhanDuoc} suất có người dùng app nên nhận được lời nhắc. Từng người một, không đăng vào nhóm — đồng ý chứ?",
    // 🔴 Câu XONG nói con số THẬT — xem docblock "HAI con số". Dùng lại `{soNguoi}` ở đây là nói dối hoặc ép rollback.
    done: "Đã nhắc riêng {soNguoiThat} người về “{tenDot}”. Ai đóng xong bạn sẽ thấy ở tiến độ đợt thu.",
  },
  journey: { outcomes: { done: { next: ["READ-COLLECTIONS"] } } },
  help: { title: "Nhắc người chưa đóng", tryNow: "nhắc mọi người đóng tiền đợt thu" },
} satisfies AiCapabilityPage;
